// src/pages/MySpouse/MySpouseHome.jsx

import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { logout } from "../../redux/actions/authActions";

import {
  Chart as ChartJS,
  BarElement,
  CategoryScale,
  LinearScale,
  ArcElement,
  Tooltip,
  Legend,
} from "chart.js";

import { Bar, Doughnut, Line } from "react-chartjs-2";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import {
  faHeart,
  faBrain,
  faBook,
  faUsers,
  faHandshake,
  faClipboardCheck,
  faChartLine,
  faRankingStar,
  faBell,
  faShieldHeart,
  faFaceSmile,
  faUserGroup,
  faComments,
  faGift,
  faFire,
  faScaleBalanced,
  faTimeline,
  faLightbulb,
  faRobot,
  faArrowTrendUp,
  faHandsHoldingCircle,
  faUserTie,
  faScroll,
} from "@fortawesome/free-solid-svg-icons";

// import MySpouseSignals from "./MySpouseSignals";
// import MySpouseChronicles from "./MySpouseChronicles";
// import MySpouseApplause from "./MySpouseApplause";
// import MySpouseTasks from "./MySpouseTasks";
// import MySpouseBooks from "./MySpouseBooks";
// import MySpouseRCA from "./MySpouseRCA";
// import MySpouseAgents from "./MySpouseAgents";
// import MySpouseInsights from "./MySpouseInsights";
// import MySpouseCommunity from "./MySpouseCommunity";

ChartJS.register(
  BarElement,
  CategoryScale,
  LinearScale,
  ArcElement,
  Tooltip,
  Legend
);

const languages = [
  { code: "en", label: "English" },
  { code: "hi", label: "हिंदी" },
  { code: "mr", label: "मराठी" },
  { code: "ta", label: "தமிழ்" },
  { code: "te", label: "తెలుగు" },
];

const MySpouseHome = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const authState = useSelector((state) => state.authReducer || {});

  const isLoggedIn = authState?.isLoggedIn || false;
  const userName = authState?.user?.name || "Partner";
  const spouseName = authState?.user?.spouseName || "Your Spouse";
  const [showConnectionInfo, setShowConnectionInfo] = useState(false);
  const [activeInfoCard, setActiveInfoCard] = useState(null);

  const [activeTab, setActiveTab] = useState("signals");
  const [selectedLanguage, setSelectedLanguage] = useState("en");
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    dispatch(logout());
    navigate("/");
  };

  // -----------------------------------
  // DEMO SIGNAL DATA
  // -----------------------------------

  const relationshipSignals = [
    {
      label: "Emotional Safety",
      value: 82,
    },
    {
      label: "Communication",
      value: 71,
    },
    {
      label: "Affection",
      value: 88,
    },
    {
      label: "Conflict Recovery",
      value: 67,
    },
    {
      label: "Trust Stability",
      value: 91,
    },
  ];

  const emotionalTrend = {
    labels: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"],
    datasets: [
      {
        label: "Relationship Health",
        data: [62, 68, 72, 66, 79, 84, 88],
        borderWidth: 3,
      },
    ],
  };

  const applauseFeed = [
    {
      id: 1,
      from: "Spouse",
      message:
        "Thank you for being emotionally available this week ❤️",
      points: 40,
    },
    {
      id: 2,
      from: "Daughter",
      message:
        "You both handled things calmly this week 🌸",
      points: 25,
    },
    {
      id: 3,
      from: "Friend",
      message:
        "Beautiful teamwork and support 👏",
      points: 18,
    },
  ];

  const chronicles = [
    {
      id: 1,
      title: "Weekend Emotional Reflection",
      mood: "Connected",
      insight:
        "Communication improved after intentional listening.",
    },
    {
      id: 2,
      title: "Conflict Recovery",
      mood: "Healing",
      insight:
        "Conflict escalated due to stress spillover from work.",
    },
  ];

  const tasks = [
    {
      id: 1,
      task: "10-minute uninterrupted conversation",
      status: "Pending",
    },
    {
      id: 2,
      task: "Express appreciation today",
      status: "Completed",
    },
    {
      id: 3,
      task: "Schedule emotional check-in",
      status: "Pending",
    },
  ];

  const tabs = [
    {
      id: "signals",
      label: "AI Signals",
      icon: faBrain,
    },
    {
      id: "chronicles",
      label: "Chronicles",
      icon: faTimeline,
    },
    {
      id: "rca",
      label: "RCA",
      icon: faLightbulb,
    },
    {
      id: "tasks",
      label: "Relationship Tasks",
      icon: faClipboardCheck,
    },
    {
      id: "applause",
      label: "Applause",
      icon: faHandsHoldingCircle,
    },
    {
      id: "books",
      label: "Immersive Books",
      icon: faBook,
    },
    {
      id: "agents",
      label: "AI Agents",
      icon: faRobot,
    },
    {
      id: "insights",
      label: "Insights",
      icon: faArrowTrendUp,
    },
    {
      id: "community",
      label: "Community",
      icon: faUserGroup,
    },
  ];

  const emotionalHealthScore = useMemo(() => {
    const total = relationshipSignals.reduce(
      (sum, s) => sum + s.value,
      0
    );

    return Math.round(total / relationshipSignals.length);
  }, [relationshipSignals]);

  return (
    <div className="min-h-screen bg-gray-950 text-gray-100 flex flex-col">

      {/* HEADER */}

      <header className="sticky top-0 z-50 bg-gray-900 border-b border-gray-800 px-4 py-4 flex justify-between">
        <div className="flex flex-col">
          <div className="flex items-center gap-2">
            <FontAwesomeIcon
              icon={faHeart}
              className="text-pink-400 text-2xl"
            />

            <div className="text-xl lg:text-3xl font-bold text-pink-200">
              MySpouse
            </div>
          </div>
          <div className="text-xs lg:text-sm text-gray-100">
              Connected with {spouseName}
          </div>
          <div className="text-[9px] lg:text-sm text-yellow-200 mt-1">
            AI Relationship Intelligence & Emotional Dynamics Platform
          </div>

          <div className="mt-3">
            <select
              value={selectedLanguage}
              onChange={(e) => setSelectedLanguage(e.target.value)}
              className="bg-gray-800 border border-gray-700 rounded-lg px-2 py-1 text-xs"
            >
              {languages.map((lang) => (
                <option key={lang.code} value={lang.code}>
                  {lang.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="hidden md:flex items-center gap-3">
          <div className="flex flex-col items-end">
            <div className="text-sm text-pink-300">
              {userName}
            </div>
          </div>

          {isLoggedIn ? (
            <button
              onClick={handleLogout}
              className="px-3 py-2 rounded-lg bg-red-600 hover:bg-red-700"
            >
              ⏻
            </button>
          ) : (
            <button
              onClick={() => navigate("/login")}
              className="px-3 py-2 rounded-lg bg-gray-800"
            >
              🔐
            </button>
          )}
        </div>

        <button
          onClick={() => setIsMobileMenuOpen((s) => !s)}
          className="md:hidden text-lg lg:text-xl text-pink-300 w-10 lg:w-12"
        >
          {isMobileMenuOpen ? "×" : "☰"}
        </button>
      </header>

      {/* TOP SIGNAL STRIP */}

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 p-4 bg-gray-900 border-b border-gray-800">
        <div className="bg-pink-900/20 rounded-2xl p-4 border border-pink-700/20 relative">
          <div className="flex items-center gap-2">
            <div className="text-xs text-gray-400">
              Relationship Health
            </div>
            <button
              onClick={() =>
                setActiveInfoCard(
                  activeInfoCard === "relationshipHealth"
                    ? null
                    : "relationshipHealth"
                )
              }
              className="w-4 h-4 rounded-full bg-pink-700 text-[10px] text-white flex items-center justify-center hover:bg-pink-600"
            >
              ℹ
            </button>
          </div>
          <div className="text-3xl font-bold text-pink-300 mt-2">
            {emotionalHealthScore}%
          </div>
        </div>

        <div className="bg-purple-900/20 rounded-2xl p-4 border border-purple-700/20 relative">
          <div className="flex items-center gap-2">
            <div className="text-xs text-gray-400">
              Emotional Safety
            </div>
            <button
              onClick={() =>
                setActiveInfoCard(
                  activeInfoCard === "emotionalSafety"
                    ? null
                    : "emotionalSafety"
                )
              }
              className="w-4 h-4 rounded-full bg-purple-700 text-[10px] text-white flex items-center justify-center hover:bg-purple-600"
            >
              ℹ
            </button>
          </div>
          <div className="text-3xl font-bold text-purple-300 mt-2">
            82%
          </div>
        </div>

        <div className="bg-blue-900/20 rounded-2xl p-4 border border-blue-700/20 relative">
          <div className="flex items-center gap-2">
            <div className="text-xs text-gray-400">
              Appreciation Score
            </div>
            <button
              onClick={() =>
                setActiveInfoCard(
                  activeInfoCard === "appreciation"
                    ? null
                    : "appreciation"
                )
              }
              className="w-4 h-4 rounded-full bg-blue-700 text-[10px] text-white flex items-center justify-center hover:bg-blue-600"
            >
              ℹ
            </button>
          </div>
          <div className="text-3xl font-bold text-blue-300 mt-2">
            91%
          </div>
        </div>

        <div className="bg-green-900/20 rounded-2xl p-4 border border-green-700/20 relative">
          <div className="flex items-center gap-2">
            <div className="text-xs text-gray-400">
              Conflict Recovery
            </div>
            <button
              onClick={() =>
                setActiveInfoCard(
                  activeInfoCard === "conflictRecovery"
                    ? null
                    : "conflictRecovery"
                )
              }
              className="w-4 h-4 rounded-full bg-green-700 text-[10px] text-white flex items-center justify-center hover:bg-green-600"
            >
              ℹ
            </button>
          </div>
          <div className="text-3xl font-bold text-green-300 mt-2">
            67%
          </div>
        </div>

        <div className="bg-yellow-900/20 rounded-2xl p-4 border border-yellow-700/20 relative">
          <div className="flex items-center gap-2">
            <div className="text-xs text-gray-400">
              Connection Streak
            </div>
            <button
              onClick={() =>
                setActiveInfoCard(
                  activeInfoCard === "connectionStreak"
                    ? null
                    : "connectionStreak"
                )
              }
              className="w-4 h-4 rounded-full bg-yellow-700 text-[10px] text-white flex items-center justify-center hover:bg-yellow-600"
            >
              ℹ
            </button>
          </div>
          <div className="text-xl lg:text-3xl font-bold text-yellow-300 mt-2">
            14-Day Streak
          </div>
        </div>
      </div>
      {activeInfoCard === "relationshipHealth" && (
        <div className="mt-3 bg-gray-900 border border-pink-700/30 rounded-xl p-3 text-xs text-gray-300 leading-5">
          <div className="text-pink-300 font-semibold mb-2">
            What is Relationship Health?
          </div>
          <div>
            Relationship Health reflects the overall emotional strength,
            communication quality, trust, appreciation, and connection
            between partners over time.
          </div>
          <ul className="list-disc ml-4 mt-2 space-y-1 [&_li]:text-gray-300">
            <li>Communication quality</li>
            <li>Emotional connection</li>
            <li>Trust and consistency</li>
            <li>Conflict handling patterns</li>
            <li>Positive relationship interactions</li>
          </ul>
          <div className="mt-2 text-pink-200">
            Higher scores may indicate stronger relationship stability and emotional bonding.
          </div>

        </div>
      )}
      {activeInfoCard === "emotionalSafety" && (
        <div className="mt-3 bg-gray-900 border border-purple-700/30 rounded-xl p-3 text-xs text-gray-300 leading-5">
          <div className="text-purple-300 font-semibold mb-2">
            What is Emotional Safety?
          </div>
          <div>
            Emotional Safety measures how comfortable and secure partners feel
            while expressing emotions, concerns, opinions, and vulnerabilities.
          </div>
          <ul className="list-disc ml-4 mt-2 space-y-1 [&_li]:text-gray-300">
            <li>Respectful communication</li>
            <li>Listening without judgment</li>
            <li>Emotional validation</li>
            <li>Support during stress</li>
            <li>Healthy conflict behavior</li>
          </ul>
          <div className="mt-2 text-purple-200">
            Strong emotional safety may improve trust, openness, and relationship resilience.
          </div>
        </div>
      )}
      {activeInfoCard === "appreciation" && (
        <div className="mt-3 bg-gray-900 border border-blue-700/30 rounded-xl p-3 text-xs text-gray-300 leading-5">
          <div className="text-blue-300 font-semibold mb-2">
            What is Appreciation Score?
          </div>
          <div>
            Appreciation Score reflects how frequently gratitude,
            acknowledgment, encouragement, and positive emotional reinforcement
            are exchanged within the relationship.
          </div>
          <ul className="list-disc ml-4 mt-2 space-y-1 [&_li]:text-gray-300">
            <li>Thank-you moments</li>
            <li>Positive affirmations</li>
            <li>Emotional encouragement</li>
            <li>Supportive interactions</li>
            <li>Recognition of efforts</li>
          </ul>
          <div className="mt-2 text-blue-200">
            Consistent appreciation may strengthen emotional connection and relationship positivity.
          </div>
        </div>
      )}
      {activeInfoCard === "conflictRecovery" && (
        <div className="mt-3 bg-gray-900 border border-green-700/30 rounded-xl p-3 text-xs text-gray-300 leading-5">
          <div className="text-green-300 font-semibold mb-2">
            What is Conflict Recovery?
          </div>
          <div>
            Conflict Recovery reflects how effectively couples resolve disagreements,
            repair emotional disconnection, and restore healthy communication.
          </div>
          <ul className="list-disc ml-4 mt-2 space-y-1 [&_li]:text-gray-300">
            <li>Repair conversations</li>
            <li>Emotional calming after conflicts</li>
            <li>Healthy apologies</li>
            <li>Resolution consistency</li>
            <li>Reduced lingering resentment</li>
          </ul>
          <div className="mt-2 text-green-200">
            Faster and healthier recovery may improve long-term relationship stability.
          </div>
        </div>
      )}
      {activeInfoCard === "connectionStreak" && (
        <div className="mt-3 bg-gray-900 border border-yellow-700/30 rounded-xl p-3 text-xs text-gray-100 leading-5">
          <div className="text-yellow-300 font-semibold mb-2">
            What is Connection Streak?
          </div>
          <div>
            A Connection Streak reflects how consistently a couple maintains
            positive emotional engagement and relationship bonding over time.
          </div>
          <ul className="list-disc ml-4 mt-2 space-y-1 [&_li]:text-gray-300">
            <li>Daily emotional check-ins</li>
            <li>Appreciation and gratitude exchanges</li>
            <li>Meaningful conversations</li>
            <li>Relationship rituals and bonding moments</li>
            <li>Healthy and positive interactions</li>
          </ul>
          <div className="mt-2 text-yellow-200">
            Stronger consistency may indicate improving emotional connection,
            communication, and relationship stability.
          </div>
        </div>
      )}

      {/* NAVIGATION TABS */}

      <div className="grid grid-cols-3 lg:grid-cols-9 gap-1 bg-gray-900 border-b border-gray-800 p-2">

        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`rounded-2xl px-2 py-3 flex flex-col items-center justify-center text-xs transition-all ${
              activeTab === tab.id
                ? "bg-pink-600 text-white"
                : "bg-gray-800 text-gray-300"
            }`}
          >
            <FontAwesomeIcon
              icon={tab.icon}
              className="text-lg mb-2"
            />

            <span>{tab.label}</span>
          </button>
        ))}
      </div>

      {/* MAIN */}

      <main className="flex-1 p-4 space-y-6 pb-20">

        {/* HERO */}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">

          {/* RELATIONSHIP DYNAMICS */}

          <div className="lg:col-span-2 bg-gray-900 rounded-3xl border border-gray-800 p-5">

            <div className="flex items-center gap-2 mb-4">
              <FontAwesomeIcon
                icon={faChartLine}
                className="text-pink-400"
              />

              <h2 className="text-xl font-bold text-pink-200">
                Relationship Dynamics Graph
              </h2>
            </div>

            <Line data={emotionalTrend} />

          </div>

          {/* AI INSIGHTS */}

          <div className="bg-gray-900 rounded-3xl border border-gray-800 p-5">

            <div className="flex items-center gap-2 mb-4">
              <FontAwesomeIcon
                icon={faBrain}
                className="text-purple-400"
              />

              <h2 className="text-lg font-bold text-purple-200">
                AI Insights
              </h2>
            </div>

            <div className="space-y-4">

              <div className="bg-purple-900/20 rounded-2xl p-3">
                <div className="text-sm text-purple-300 font-semibold">
                  Emotional Drift Detected
                </div>

                <div className="text-xs text-gray-300 mt-1">
                  Reduced appreciation patterns detected over
                  last 5 days.
                </div>
              </div>

              <div className="bg-blue-900/20 rounded-2xl p-3">
                <div className="text-sm text-blue-300 font-semibold">
                  Repair Opportunity
                </div>

                <div className="text-xs text-gray-300 mt-1">
                  Suggest initiating calm emotional check-in.
                </div>
              </div>

              <div className="bg-green-900/20 rounded-2xl p-3">
                <div className="text-sm text-green-300 font-semibold">
                  Appreciation Momentum
                </div>

                <div className="text-xs text-gray-300 mt-1">
                  Positive emotional reinforcement improving.
                </div>
              </div>

            </div>

          </div>

        </div>

        {/* RELATIONSHIP SIGNALS */}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

          {/* SIGNAL CHART */}

          <div className="bg-gray-900 rounded-3xl border border-gray-800 p-5">

            <div className="flex items-center gap-2 mb-4">
              <FontAwesomeIcon
                icon={faHeart}
                className="text-pink-400"
              />

              <h2 className="text-xl font-bold text-pink-200">
                Relationship Signals
              </h2>
            </div>

            <Bar
              data={{
                labels: relationshipSignals.map((s) => s.label),
                datasets: [
                  {
                    label: "Signals",
                    data: relationshipSignals.map((s) => s.value),
                    borderWidth: 1,
                  },
                ],
              }}
            />
          </div>

          {/* APPLAUSE FEED */}

          <div className="bg-gray-900 rounded-3xl border border-gray-800 p-5">

            <div className="flex items-center gap-2 mb-4">
              <FontAwesomeIcon
                icon={faHandsHoldingCircle}
                className="text-yellow-400"
              />

              <h2 className="text-xl font-bold text-yellow-200">
                Applause & Appreciation
              </h2>
            </div>

            <div className="space-y-3">

              {applauseFeed.map((item) => (
                <div
                  key={item.id}
                  className="bg-gray-800 rounded-2xl p-4"
                >
                  <div className="flex items-center justify-between">

                    <div className="text-sm text-yellow-300 font-semibold">
                      {item.from}
                    </div>

                    <div className="text-xs text-pink-300">
                      +{item.points} ❤️
                    </div>

                  </div>

                  <div className="text-sm text-gray-300 mt-2">
                    {item.message}
                  </div>
                </div>
              ))}

            </div>
          </div>
        </div>

        {/* CHRONICLES + TASKS */}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

          {/* CHRONICLES */}

          <div className="bg-gray-900 rounded-3xl border border-gray-800 p-5">

            <div className="flex items-center gap-2 mb-4">
              <FontAwesomeIcon
                icon={faScroll}
                className="text-blue-400"
              />

              <h2 className="text-xl font-bold text-blue-200">
                Relationship Chronicles
              </h2>
            </div>

            <div className="space-y-3">

              {chronicles.map((c) => (
                <div
                  key={c.id}
                  className="bg-gray-800 rounded-2xl p-4"
                >
                  <div className="flex items-center justify-between">

                    <div className="font-semibold text-gray-200">
                      {c.title}
                    </div>

                    <div className="text-xs text-pink-300">
                      {c.mood}
                    </div>

                  </div>

                  <div className="text-sm text-gray-400 mt-2">
                    {c.insight}
                  </div>
                </div>
              ))}

            </div>

          </div>

          {/* TASKS */}

          <div className="bg-gray-900 rounded-3xl border border-gray-800 p-5">

            <div className="flex items-center gap-2 mb-4">
              <FontAwesomeIcon
                icon={faClipboardCheck}
                className="text-green-400"
              />

              <h2 className="text-xl font-bold text-green-200">
                Relationship Rituals & Tasks
              </h2>
            </div>

            <div className="space-y-3">

              {tasks.map((task) => (
                <div
                  key={task.id}
                  className="bg-gray-800 rounded-2xl p-4 flex items-center justify-between"
                >
                  <div className="text-sm text-gray-200">
                    {task.task}
                  </div>

                  <div
                    className={`text-xs px-3 py-1 rounded-full ${
                      task.status === "Completed"
                        ? "bg-green-700 text-white"
                        : "bg-yellow-700 text-white"
                    }`}
                  >
                    {task.status}
                  </div>
                </div>
              ))}

            </div>

          </div>

        </div>

        {/* DYNAMIC TAB MODULES */}

        {/* {activeTab === "signals" && (
          <MySpouseSignals selectedLanguage={selectedLanguage} />
        )}

        {activeTab === "chronicles" && (
          <MySpouseChronicles selectedLanguage={selectedLanguage} />
        )}

        {activeTab === "rca" && (
          <MySpouseRCA selectedLanguage={selectedLanguage} />
        )}

        {activeTab === "tasks" && (
          <MySpouseTasks selectedLanguage={selectedLanguage} />
        )}

        {activeTab === "applause" && (
          <MySpouseApplause selectedLanguage={selectedLanguage} />
        )}

        {activeTab === "books" && (
          <MySpouseBooks selectedLanguage={selectedLanguage} />
        )}

        {activeTab === "agents" && (
          <MySpouseAgents selectedLanguage={selectedLanguage} />
        )}

        {activeTab === "insights" && (
          <MySpouseInsights selectedLanguage={selectedLanguage} />
        )}

        {activeTab === "community" && (
          <MySpouseCommunity selectedLanguage={selectedLanguage} />
        )} */}

      </main>

      {/* FOOTER */}

      <footer className="bg-gray-900 border-t border-gray-800 py-6 text-center text-gray-500 text-sm">
        © 2026 Think5Whys Global DeepTech Solutions • MySpouse AI Relationship Intelligence Platform
      </footer>

    </div>
  );
};

export default MySpouseHome;